/**
 * Surfaces connector health alerts (from the backend notifier's
 * `/api/alerts` live state) as in-app notifications that DEEP LINK to the
 * failing row: clicking one calls
 * `window.openSettings('connections', { connector })`, which lands on
 * /settings/connections with the row scrolled into view, expanded, and
 * flashing — the healthScheduler's "fix it on Settings → Connections"
 * prose finally carries its link (settings redesign 2026-08, item #8).
 *
 * Shares the ['active-alerts'] query with useScraperLoginAlert — one poll
 * serves both. Dedupes per (fingerprint, last_fired_at) so a persistent
 * alert notifies once per firing, not once per poll.
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/apiClient';
import { useRole } from './useRole';

const CONNECTOR_KINDS = new Set([
  'connector-unhealthy',
  'connector-auto-disabled',
  'connector-recovered',
]);

export function useConnectorHealthAlerts() {
  const { isAdmin } = useRole();
  const seen = useRef(new Set());

  const { data } = useQuery({
    queryKey: ['active-alerts'],
    queryFn: () => apiClient.get('/api/alerts'),
    enabled: !!isAdmin,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 0,
  });

  useEffect(() => {
    const alerts = (data?.alerts || []).filter((a) => CONNECTOR_KINDS.has(a.kind));
    for (const alert of alerts) {
      const ctx = alert.current_context || alert.context || {};
      const dedupeKey = `${alert.fingerprint || alert.kind}:${alert.last_fired_at || ''}`;
      if (seen.current.has(dedupeKey) || !window.addNotification) continue;
      seen.current.add(dedupeKey);
      window.addNotification({
        type: alert.severity === 'info' ? 'connector' : 'error',
        message: alert.current_message || alert.message,
        connector: ctx.connector || null,
        onClick: ctx.connector
          ? () => window.openSettings?.('connections', { connector: ctx.connector })
          : undefined,
      });
    }
  }, [data]);
}

export default useConnectorHealthAlerts;
